// 在文件顶部添加导入或引用其他模块的语句
// 确保文件被视为模块而不是脚本

/**
 * 创建无限滚动效果
 * @param {Object} options - 配置选项
 * @param {HTMLElement} options.scrollTrack - 包含所有卡片的轨道元素
 * @param {HTMLElement} options.scrollContainer - 滚动容器元素
 * @param {number} options.cardWidth - 卡片宽度(像素)
 * @param {number} options.duration - 滚动一个完整卡片的时间(秒)
 * @param {function} [options.onPause] - 暂停时的回调函数
 * @param {function} [options.onResume] - 恢复滚动时的回调函数
 * @returns {Object} - 包含控制方法的对象
 */

// 为 window.gsap 添加类型定义
interface GsapInstance {
  to: (
    target: HTMLElement,
    vars: {
      [key: string]: any;
      duration?: number;
      ease?: string;
      onComplete?: () => void;
    },
  ) => GsapAnimation;
  set: (target: HTMLElement, properties: { [key: string]: any }) => void;
}

interface GsapAnimation {
  kill: () => void;
  pause: () => void;
  play: () => void;
}

// 确保全局声明在模块上下文中
declare global {
  interface Window {
    gsap: GsapInstance;
  }
}

// 定义选项接口
interface InfiniteScrollOptions {
  scrollTrack: HTMLElement;
  scrollContainer: HTMLElement;
  cardWidth: number;
  duration?: number;
  onPause?: () => void;
  onResume?: () => void;
}

// 定义返回值接口
interface InfiniteScrollControls {
  start: () => GsapAnimation;
  pause: () => void;
  resume: () => void;
  destroy: () => void;
}

/**
 * 创建无限滚动效果
 */
function createInfiniteScroll(
  options: InfiniteScrollOptions,
): InfiniteScrollControls {
  // 解构参数并设置默认值
  const {
    scrollTrack,
    scrollContainer,
    cardWidth,
    duration = 2,
    onPause = () => {},
    onResume = () => {},
  } = options;

  // 验证必要参数
  if (!scrollTrack || !scrollContainer || !cardWidth) {
    throw new Error("必须提供scrollTrack、scrollContainer和cardWidth参数");
  }

  // 内部状态
  let animation: GsapAnimation | null = null;
  let isActive = false;

  // 计算滚动速度（像素/秒）
  const scrollSpeed = cardWidth / duration;

  /**
   * 创建滚动动画
   * @param {number} distance - 滚动距离
   * @param {number} animDuration - 动画持续时间
   * @returns {gsap.core.Tween} - GSAP动画实例
   */
  function createScrollAnimation(
    distance: number,
    animDuration: number,
  ): GsapAnimation {
    return window.gsap.to(scrollContainer, {
      scrollLeft: `+=${distance}`,
      duration: animDuration,
      ease: "none",
      onComplete: () => {
        // 移动第一个卡片到末尾
        if (scrollTrack.firstElementChild) {
          scrollTrack.appendChild(
            scrollTrack.removeChild(
              scrollTrack.firstElementChild as Node,
            ) as Node,
          );
        }

        // 重置滚动位置并继续动画
        window.gsap.set(scrollContainer, { scrollLeft: 0 });
        if (isActive) {
          startScrolling();
        }
      },
    });
  }

  /**
   * 开始滚动动画
   */
  function startScrolling(): GsapAnimation {
    // 清理现有动画
    if (animation) {
      animation.kill();
    }

    isActive = true;

    // 处理当前滚动状态
    const currentScrollLeft = scrollContainer.scrollLeft;
    if (currentScrollLeft !== null && currentScrollLeft > 0) {
      // 计算已滚动卡片数和余数
      const currentCardIndex = Math.floor(currentScrollLeft / cardWidth);
      const remainder = currentScrollLeft % cardWidth;

      // 移动卡片并重置滚动位置
      for (
        let i = 0;
        i < currentCardIndex && scrollTrack.firstElementChild;
        i++
      ) {
        scrollTrack.appendChild(
          scrollTrack.removeChild(
            scrollTrack.firstElementChild as Node,
          ) as Node,
        );
      }

      window.gsap.set(scrollContainer, { scrollLeft: remainder });

      // 设置下一次滚动距离
      const distance = cardWidth - remainder;
      // 根据距离和速度计算持续时间，确保速度一致
      const animDuration = distance / scrollSpeed;
      animation = createScrollAnimation(distance, animDuration);
    } else {
      // 默认滚动一个卡片宽度，使用标准持续时间
      animation = createScrollAnimation(cardWidth, duration);
    }

    return animation;
  }

  /**
   * 暂停滚动
   */
  function pause(): void {
    if (animation) {
      animation.kill();
      animation = null;
    }
    isActive = false;
    onPause();
  }

  /**
   * 恢复滚动
   */
  function resume(): void {
    if (!isActive) {
      startScrolling();
      onResume();
    }
  }

  /**
   * 销毁滚动器，清理资源
   */
  function destroy(): void {
    // 移除所有事件监听器
    scrollContainer.removeEventListener("mouseenter", pause);
    scrollContainer.removeEventListener("mouseleave", resume);

    // 停止动画
    if (animation) {
      animation.kill();
      animation = null;
    }

    isActive = false;
  }

  // 设置鼠标事件监听
  scrollContainer.addEventListener("mouseenter", pause);
  scrollContainer.addEventListener("mouseleave", resume);

  // 初始启动动画
  startScrolling();

  // 返回控制接口
  return {
    start: startScrolling,
    pause,
    resume,
    destroy,
  };
}

// 导出函数，确保文件被视为模块
export default createInfiniteScroll;
